import React, { useState, useRef, useEffect } from 'react'
import {
  Button,
  Card,
  Grid,
  MenuItem,
  Divider,
  IconButton,
  makeStyles,
  Typography,
  Chip
} from '@material-ui/core'
import { FieldArray, Form, Formik } from 'formik'
import { pollFormSchema } from 'schemas/polling-1'
import { Letters, PollStatus, speakerStatus, SubjectStatus } from 'constants/index'
import {
  addPollAlongWithQuestions,
  fetchPollById,
  resetPollById,
  updatePollAlongWithQuestions
} from 'store/polling'
import CustomTextField from 'components/Form/CustomTextField'
import { Box } from '@mui/material'
import { Add, AddCircle, Close, DoDisturbOnOutlined } from '@material-ui/icons'
import { nanoid } from 'nanoid'
import { useDispatch, useSelector } from 'react-redux'
import ProgressButton from 'components/ProgressButton'
import { colors } from 'utils/colors'
import { updateSubjectStatus } from 'store/subject'
import Tooltip from '@material-ui/core/Tooltip'
import { toast } from 'react-toastify'
import TextField from '@material-ui/core/TextField'
import CustomPopup from 'components/CustomPopup'
import SpeakerForm from 'views/admin/speakers/SpeakerForm'
import { getSpeakers } from 'store/speaker'
import { caseInsensitiveSort } from 'utils'
import ConfirmModal from 'components/ConfirmationModal'

const PollingForm = ({
  editedPollId,
  pollStatus,
  pollValues,
  forApproval,
  speakerId,
  conferenceId,
  subjectId,
  handleCloseModal,
  handleCloseConfirmModal,
  associateSpeaker,
  isCustomSubmit,
  handleCustomSubmit,
  reloadPolls,
  onSuccess
}) => {
  const speakerInputRef = useRef(null)
  const dispatch = useDispatch()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [resettingPoll, setResettingPoll] = useState(false)
  const [openResetPoll, setOpenResetPoll] = useState(false)
  const [speakers, setSpeakers] = useState([])
  const [openSpeakerModal, setOpenSpeakerModal] = useState(false)
  const { user } = useSelector((state) => state.auth)

  useEffect(() => {
    const unsubSpeakers = getSpeakers(user?.conferenceId, (data) => {
      setSpeakers(caseInsensitiveSort(data, 'fullName'))
    })
    return () => unsubSpeakers && unsubSpeakers()
  }, [user?.conferenceId])

  const emptyQuestion = {
    title: '',
    options: [
      { optId: nanoid(), opt: '' },
      { optId: nanoid(), opt: '' }
    ]
  }

  const resetPoll = async () => {
    setResettingPoll(true)
    await dispatch(resetPollById(editedPollId))
    setResettingPoll(false)
  }

  const handleSubmit = async (values) => {
    setLoading(true)
    const updatedValues = {
      ...values,
      conferenceId,
      status: PollStatus.CREATED
    }
    if (!associateSpeaker) {
      speakerId ? (updatedValues.speakerId = speakerId) : (updatedValues.subjectId = subjectId)
    }
    if (editedPollId) {
      const poll = await fetchPollById(editedPollId)
      if (poll.status === PollStatus.CLOSED) {
        handleCloseConfirmModal()
        toast.error('You can not update the poll as it has already been concluded.')
        return
      } else if (poll.status !== PollStatus.CREATED) {
        handleCloseConfirmModal()
        toast.error('You can not update the poll as it is in progress.')
        return
      }
      const missingQuestionIds = pollValues?.questions
        .filter(
          (initialQuestion) =>
            !updatedValues.questions.some(
              (updatedQuestion) => updatedQuestion.id === initialQuestion.id
            )
        )
        .map((question) => question.id)
      await dispatch(updatePollAlongWithQuestions(editedPollId, updatedValues, missingQuestionIds))
      toast.success('The poll along with the questions updated successfully.')
    } else {
      const pollId = await dispatch(addPollAlongWithQuestions(updatedValues))
      reloadPolls && (await reloadPolls())
      if (onSuccess) {
        await onSuccess(pollId, values.speakerId)
      } else toast.success('The poll along with the questions added successfully.')
    }
    if (!forApproval && !speakerId) {
      await dispatch(updateSubjectStatus(subjectId, SubjectStatus.AWAITING_RESPONSE))
    }
    setLoading(false)
    handleCloseConfirmModal()
  }

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={associateSpeaker ? { ...pollValues, speakerId: '' } : pollValues}
        validationSchema={pollFormSchema({ associateSpeaker: associateSpeaker })}
        onSubmit={async (values) =>
          isCustomSubmit ? handleCustomSubmit(values) : await handleSubmit(values)
        }>
        {({ values, touched, errors, setFieldValue }) => (
          <Form>
            <Box className={classes.modalHeader}>
              <Typography className={classes.modalTitle}>
                {editedPollId ? 'Edit Poll Details' : 'Add new Poll'}
              </Typography>
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                {editedPollId && pollStatus !== PollStatus.CREATED && (
                  <Button
                    variant="contained"
                    onClick={() => setOpenResetPoll(true)}
                    className={classes.resetPollBtn}>
                    Reset Poll
                  </Button>
                )}
                <ProgressButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || (editedPollId && pollStatus !== PollStatus.CREATED)}
                  label={editedPollId ? 'Update Poll' : 'Create Poll'}
                  inProgress={loading}
                />
                <Tooltip title="Close">
                  <IconButton className={classes.closeBtn} onClick={() => handleCloseModal(values)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Grid
              container
              spacing={2}
              className={editedPollId && pollStatus !== PollStatus.CREATED ? classes.disabled : ''}>
              <Grid item xs={associateSpeaker ? 6 : 12}>
                <CustomTextField
                  name="title"
                  type="text"
                  placeholder="Enter poll title here..."
                  label="Poll Title"
                  disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                />
              </Grid>
              {associateSpeaker && (
                <Grid item xs={6}>
                  <TextField
                    ref={speakerInputRef}
                    className={classes.selectSpeakerMenu}
                    select
                    fullWidth
                    name="speakerId"
                    label="Select a Speaker"
                    variant="outlined"
                    disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                    error={Boolean(touched.speakerId && errors.speakerId)}
                    value={values.speakerId}
                    onChange={(event) =>
                      event.target.value && setFieldValue('speakerId', event.target.value)
                    }
                    helperText={touched.speakerId && errors.speakerId ? errors.speakerId : null}>
                    {speakers?.map((speaker) => (
                      <MenuItem value={speaker.id} key={speaker.id} className={classes.listItem}>
                        {speaker?.isSubject && speaker?.fullName
                          ? speaker?.fullName
                          : speaker?.isSubject && !speaker?.fullName
                          ? speaker.email
                          : speaker.fullName}
                        {speaker?.isSubject && speaker?.status === speakerStatus.PENDING && (
                          <Chip
                            size="small"
                            className={classes.chip}
                            color="primary"
                            label={
                              <Typography className={classes.chipLabel}>
                                {speaker?.status === speakerStatus.PENDING
                                  ? 'Not Approved'
                                  : 'Approved'}
                              </Typography>
                            }
                          />
                        )}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        if (speakerInputRef.current) {
                          speakerInputRef.current.blur()
                        }
                        setOpenSpeakerModal(true)
                      }}>
                      <Typography className={classes.buttonItem}>
                        <Add />
                        Create speaker
                      </Typography>
                    </MenuItem>
                  </TextField>
                </Grid>
              )}
            </Grid>

            <Box
              className={editedPollId && pollStatus !== PollStatus.CREATED ? classes.disabled : ''}>
              <Typography className={classes.questionHeader}>Poll Questions</Typography>
              <FieldArray name="questions">
                {({ push, remove }) => (
                  <Box>
                    {values.questions?.map((question, index) => (
                      <Card elevation={0} className={classes.questionCard} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <CustomTextField
                            type="text"
                            name={`questions.${index}.title`}
                            label={`Question No ${index + 1}`}
                            placeholder="Write your question title here..."
                            disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                          />
                          {values?.questions?.length > 1 && (
                            <Tooltip title="Delete Question">
                              <IconButton
                                onClick={() => {
                                  remove(index)
                                }}>
                                <DoDisturbOnOutlined />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <FieldArray name={`questions.${index}.options`}>
                          {({ push: pushOption, remove: removeOption }) => (
                            <Box className={classes.questionOptions}>
                              {question?.options?.map((option, optionIndex) => (
                                <Box key={optionIndex + option.optId}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <Typography>{Letters[optionIndex]}.</Typography>
                                    <CustomTextField
                                      type="text"
                                      name={`questions.${index}.options.${optionIndex}.opt`}
                                      placeholder={`Option ${Letters[optionIndex]}`}
                                      disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                                    />
                                    {question?.options?.length > 2 && (
                                      <Tooltip title="Delete Option">
                                        <IconButton
                                          onClick={() => {
                                            removeOption(optionIndex)
                                          }}>
                                          <DoDisturbOnOutlined />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  {optionIndex < 6 &&
                                    optionIndex === question?.options?.length - 1 && (
                                      <Box sx={{ marginTop: '16px' }}>
                                        <Button
                                          className={classes.addBtn}
                                          color="primary"
                                          variant="contained"
                                          onClick={() => pushOption({ optId: nanoid(), opt: '' })}
                                          disabled={
                                            editedPollId && pollStatus !== PollStatus.CREATED
                                          }
                                          startIcon={<AddCircle />}>
                                          Add Option
                                        </Button>
                                      </Box>
                                    )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </FieldArray>
                      </Card>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                      <Button
                        variant="contained"
                        className={classes.addQuestionBtn}
                        onClick={() => push(emptyQuestion)}
                        startIcon={<AddCircle />}
                        disabled={editedPollId && pollStatus !== PollStatus.CREATED}>
                        Add Question
                      </Button>
                    </Box>
                  </Box>
                )}
              </FieldArray>
            </Box>

            <Box className={classes.pollBtnBox}>
              <Button disabled={loading} onClick={() => handleCloseModal(values)}>
                Cancel
              </Button>
              <ProgressButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || (editedPollId && pollStatus !== PollStatus.CREATED)}
                label={editedPollId ? 'Update Poll' : 'Create Poll'}
                inProgress={loading}
              />
            </Box>
            <CustomPopup
              title="Create speaker"
              maxWidth="lg"
              open={openSpeakerModal}
              onClose={() => setOpenSpeakerModal(false)}>
              <SpeakerForm
                displayContext="modal"
                onCloseModal={() => setOpenSpeakerModal(false)}
                onSuccess={(speakerId) => {
                  setOpenSpeakerModal(false)
                  setFieldValue('speakerId', speakerId)
                }}
              />
            </CustomPopup>
            <ConfirmModal
              dialogTitle="Reset poll"
              dialogText="You're about to reset this poll, which may have responses to the questions. All existing answers will be lost. Are you sure you want to continue?"
              open={Boolean(openResetPoll)}
              closeModal={() => setOpenResetPoll(false)}
              handleConfirm={async () => {
                await resetPoll()
                handleCloseModal(values)
                toast.success('The poll has been reset successfully.')
              }}
              inProgress={resettingPoll}
            />
          </Form>
        )}
      </Formik>
    </Box>
  )
}

export default PollingForm

const useStyles = makeStyles({
  chipLabel: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    fontWeight: 'bold'
  },
  selectSpeakerMenu: {
    '& .MuiOutlinedInput-root': {
      '& .MuiSelect-outlined.MuiSelect-outlined': {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }
    }
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  chip: {
    background: '#c62828',
    color: '#FFFFFF'
  },
  modalHeader: {
    paddingTop: '20px',
    paddingBottom: '10px',
    position: 'sticky',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    background: 'white',
    top: '0px',
    zIndex: 999
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.7
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 'bold'
  },
  closeBtn: {
    padding: '10px'
  },
  addBtn: {
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  resetPollBtn: {
    color: 'white',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    backgroundColor: '#d32f2f',
    '&:hover': {
      backgroundColor: '#c62828'
    }
  },
  addQuestionBtn: {
    textTransform: 'none',
    color: 'white',
    fontWeight: 'bold',
    backgroundColor: '#fb8c00',
    '&:hover': {
      backgroundColor: '#ef6c00'
    }
  },
  progressBtnGrid: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  pollBtnBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',
    justifyContent: 'flex-end',
    paddingBottom: '16px'
  },
  questionOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '700px',
    paddingLeft: '30px'
  },
  questionHeader: {
    marginTop: '16px',
    fontWeight: 'bold',
    fontSize: '17px',
    color: colors.indigo
  },
  questionCard: {
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
    border: '1px solid #bdbdbd',
    borderRadius: '10px'
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  buttonItem: {
    color: colors.indigo,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem'
  }
})
